/**
 * Onboarding User Detail Page
 *
 * Shows full detail for a single pending_org user.
 * Reached by clicking a user row in the stage drill-down.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { founderDashboardAPI } from '../../../services/api';
import { useEnvironment } from '../../../context/EnvironmentContext';
import RefreshIndicator from '../../../components/founder-dashboard/RefreshIndicator';
import {
  ArrowLeftIcon,
  UserIcon,
  EnvelopeIcon,
  CalendarIcon,
  ClockIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

// All possible stages in canonical display order.
const STAGE_ORDER = [
  'signed_up',
  'viewed_options',
  'onboarding_skipped',
  'searched_church',
  'notify_me',
  'lead_submitted',
  'join_code_attempted',
  'join_request_sent',
  'awaiting_approval',
  'invited',
  'personal_group_started',
  'org_creation_started',
  'activated',
];

const STAGE_LABELS = {
  signed_up: 'Signed Up',
  viewed_options: 'Opened Wizard',
  onboarding_skipped: 'Closed Wizard',
  searched_church: 'Searched Church',
  notify_me: 'Notify Me',
  lead_submitted: 'Submitted Lead',
  join_code_attempted: 'Tried Join Code',
  join_request_sent: 'Sent Join Request',
  awaiting_approval: 'Awaiting Approval',
  invited: 'Invited',
  personal_group_started: 'Personal Group',
  org_creation_started: 'Started Org Creation',
  activated: 'Activated',
};

const STAGE_BADGE_COLORS = {
  signed_up: 'bg-gray-100 text-gray-800 border-gray-200',
  viewed_options: 'bg-blue-100 text-blue-800 border-blue-200',
  onboarding_skipped: 'bg-orange-100 text-orange-800 border-orange-200',
  searched_church: 'bg-purple-100 text-purple-800 border-purple-200',
  notify_me: 'bg-slate-100 text-slate-800 border-slate-200',
  lead_submitted: 'bg-teal-100 text-teal-800 border-teal-200',
  join_code_attempted: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  join_request_sent: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  awaiting_approval: 'bg-amber-100 text-amber-800 border-amber-200',
  invited: 'bg-lime-100 text-lime-800 border-lime-200',
  personal_group_started: 'bg-violet-100 text-violet-800 border-violet-200',
  org_creation_started: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  activated: 'bg-green-100 text-green-800 border-green-200',
};

const EVENT_LABELS = {
  church_search: 'Searched for a church',
  church_search_performed: 'Searched for a church',
  search_no_results: 'Search returned no results',
  join_request_submitted: 'Sent join request',
  join_request_approved: 'Join request approved',
  join_request_denied: 'Join request denied',
  lead_capture_submitted: 'Submitted church interest form',
  onboarding_stage_advanced: 'Stage advanced',
  onboarding_skipped: 'Closed Getting Started wizard',
  join_code_attempted: 'Tried a join code',
  viewed_options: 'Opened Getting Started wizard',
  org_creation_started: 'Started creating an organization',
  user_signup: 'Created account',
  invitation_accepted: 'Accepted invitation',
  join_code_awaiting_approval: 'Awaiting leader approval',
  personal_group_wizard_started: 'Started personal group wizard',
  personal_group_cta_tapped: 'Tapped "Start a group" CTA',
  personal_group_created: 'Personal group created',
  personal_group_wizard_step2_shown: 'Invite step shown',
  personal_group_invite_shared: 'Shared invite via share sheet',
  personal_group_invite_emails_sent: 'Sent email invites',
  personal_group_wizard_step2_skipped: 'Skipped invite step',
  personal_group_wizard_completed: 'Finished personal group wizard',
};

const EVENT_ICONS = {
  church_search: MagnifyingGlassIcon,
  church_search_performed: MagnifyingGlassIcon,
  search_no_results: ExclamationTriangleIcon,
  join_request_submitted: PaperAirplaneIcon,
  join_request_approved: CheckCircleIcon,
  join_request_denied: ExclamationTriangleIcon,
  lead_capture_submitted: DocumentTextIcon,
  onboarding_stage_advanced: ArrowRightIcon,
  onboarding_skipped: ArrowRightIcon,
  join_code_attempted: ArrowRightIcon,
  viewed_options: ArrowRightIcon,
  org_creation_started: ArrowRightIcon,
  user_signup: UserIcon,
  invitation_accepted: CheckCircleIcon,
  join_code_awaiting_approval: ClockIcon,
  personal_group_wizard_started: ArrowRightIcon,
  personal_group_cta_tapped: ArrowRightIcon,
  personal_group_created: CheckCircleIcon,
  personal_group_wizard_step2_shown: ArrowRightIcon,
  personal_group_invite_shared: PaperAirplaneIcon,
  personal_group_invite_emails_sent: EnvelopeIcon,
  personal_group_wizard_step2_skipped: ArrowRightIcon,
  personal_group_wizard_completed: CheckCircleIcon,
};

function daysSince(dateStr) {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  // Clamp at 0 — a negative value (clock skew) is never meaningful here.
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
}

function formatTimestamp(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getMetadataDisplay(event) {
  const meta = event.metadata || event.details || {};
  const eventType = event.actionType || event.type || '';
  const parts = [];

  // Church name (resolved from orgId on backend) — show first for context
  const churchName = meta.organizationName || meta.orgName;

  // Church search
  if (meta.query || meta.searchQuery) {
    parts.push(`Searched for: "${meta.query || meta.searchQuery}"`);
    if (meta.resultCount !== undefined) parts.push(`${meta.resultCount} results found`);
  }

  // Join request / approval — show which church
  if (eventType === 'join_request_submitted' && churchName) {
    parts.push(churchName);
  }
  if (eventType === 'join_request_approved') {
    if (churchName) parts.push(churchName);
  }
  if (eventType === 'join_request_denied') {
    if (churchName) parts.push(churchName);
    if (meta.reason) parts.push(`Reason: ${meta.reason}`);
  }

  // Join code
  if (meta.joinCode) parts.push(`Code: ${meta.joinCode}`);

  // Admin notes on approval
  if (meta.adminNotes) parts.push(`Notes: ${meta.adminNotes}`);

  // Awaiting approval — show group name
  if (meta.groupName) parts.push(`Group: ${meta.groupName}`);

  // Invitation accepted
  if (eventType === 'invitation_accepted') {
    if (meta.invitedRole) parts.push(`Role: ${meta.invitedRole.replace(/_/g, ' ')}`);
  }

  return parts.length > 0 ? parts.join(' · ') : null;
}

export default function OnboardingUserDetailPage() {
  const { currentEnv } = useEnvironment();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataEnv, setDataEnv] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await founderDashboardAPI.getOnboardingUserDetail(userId);
      setData(response.data);
      setDataEnv(currentEnv);
    } catch (err) {
      console.error('[OnboardingUserDetailPage] Error:', err);
      const errorData = err.response?.data?.error;
      const errorMsg =
        typeof errorData === 'string'
          ? errorData
          : errorData?.message || err.message || 'Couldn\'t load this person\'s details. Try again?';
      setError(errorMsg);
      setDataEnv(currentEnv);
    } finally {
      setLoading(false);
    }
  }, [currentEnv, userId]);

  useEffect(() => {
    setData(null);
    setError(null);
    fetchData();
  }, [fetchData]);

  const user = useMemo(() => data?.user || {}, [data]);
  const timeline = useMemo(() => data?.timeline || data?.journey || [], [data]);
  const currentStage = user.currentStage || user.onboardingStage || 'signed_up';
  const passedStages = data?.passedStages || user.passedStages || [];
  const daysSinceSignup = daysSince(user.signupDate || user.createdAt);
  const stateEnteredAt = user.stateEnteredAt || user.state_entered_at || user.stageEnteredAt;
  const daysAtCurrentStage = user.daysAtStage ?? daysSince(stateEnteredAt);

  // Determine which stages are completed, current, or future
  // Build evidence from timeline events — only mark a stage completed if
  // the user actually reached it, not based on position in the list.
  const currentStageIdx = STAGE_ORDER.indexOf(currentStage);

  // Map of stage → earliest timestamp from timeline events
  const evidencedStages = useMemo(() => {
    const EVENT_TO_STAGE = {
      user_signup: 'signed_up',
      onboarding_skipped: 'onboarding_skipped',
      viewed_options: 'viewed_options',
      join_code_attempted: 'join_code_attempted',
      church_search: 'searched_church',
      church_search_performed: 'searched_church',
      search_no_results: 'searched_church',
      join_request_submitted: 'join_request_sent',
      join_code_awaiting_approval: 'awaiting_approval',
      // NOTE: lead_capture_submitted is handled as a special case below so the
      // event routes to notify_me vs lead_submitted based on submission content.
      org_creation_started: 'org_creation_started',
      join_request_approved: 'invited',
      invitation_accepted: 'activated',
    };

    const stages = new Map();
    // Everyone signed up — use signup date as timestamp
    stages.set('signed_up', user.signupDate || user.createdAt || null);
    for (const event of timeline) {
      const eventType = event.actionType || event.type || '';
      const ts = event.timestamp || event.createdAt;

      // Route lead_capture_submitted to the correct row. Prefer the explicit
      // isNotifyMe flag (emitted server-side from 2026-04-13 onward). Historical
      // events pre-fix won't have it — fall back to inferring from city/state.
      if (eventType === 'lead_capture_submitted') {
        const meta = event.metadata || {};
        const isNotifyMe = meta.isNotifyMe ?? (!meta.city && !meta.state);
        const stage = isNotifyMe ? 'notify_me' : 'lead_submitted';
        if (!stages.has(stage)) stages.set(stage, ts);
        continue;
      }

      const mapped = EVENT_TO_STAGE[eventType];
      if (mapped && !stages.has(mapped)) stages.set(mapped, ts);
      if (eventType === 'onboarding_stage_advanced' && event.metadata?.newStage) {
        const s = event.metadata.newStage;
        if (!stages.has(s)) stages.set(s, ts);
      }
    }
    return stages;
  }, [timeline, user]);

  function getStageStatus(stage) {
    if (stage === currentStage) return 'current';
    if (passedStages.includes(stage)) return 'completed';
    if (evidencedStages.has(stage)) return 'completed';
    return 'future';
  }

  function formatStagetime(dateStr) {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  // Keep hooks above this branch so the render order stays stable when a
  // request transitions from loading to an error.
  if (error && dataEnv === currentEnv) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 min-h-[44px]"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          Back
        </button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium">Couldn't load details</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors min-h-[44px]"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb + Header */}
      <div className="flex items-center justify-between">
        <div>
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-sm text-gray-500 mb-2">
            <button
              onClick={() => navigate('/onboarding')}
              className="hover:text-gray-900 min-h-[44px] flex items-center"
            >
              Onboarding
            </button>
            <ChevronRightIcon className="w-3.5 h-3.5" />
            <span className="text-gray-900 font-medium">
              {loading ? 'Loading...' : user.name || user.email || 'User Detail'}
            </span>
          </nav>

          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2 min-h-[44px]"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>

          {loading ? (
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
          ) : (
            <h2 className="text-2xl font-bold text-gray-900">
              {user.name || user.email || 'Unknown User'}
            </h2>
          )}
        </div>
        <RefreshIndicator onRefresh={fetchData} loading={loading} />
      </div>

      {/* Section 1: User Profile Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        {loading ? (
          <div className="animate-pulse space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gray-200 rounded-full" />
              <div className="space-y-2 flex-1">
                <div className="h-6 bg-gray-200 rounded w-1/3" />
                <div className="h-4 bg-gray-200 rounded w-1/4" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="h-16 bg-gray-100 rounded-lg" />
              <div className="h-16 bg-gray-100 rounded-lg" />
              <div className="h-16 bg-gray-100 rounded-lg" />
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <UserIcon className="w-8 h-8 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-semibold text-gray-900">
                  {user.name || 'No name provided'}
                </h3>
                <p className="text-gray-500">{user.email || '-'}</p>
                <div className="mt-2">
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${
                      STAGE_BADGE_COLORS[currentStage] || 'bg-gray-100 text-gray-800 border-gray-200'
                    }`}
                  >
                    {STAGE_LABELS[currentStage] || currentStage}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <CalendarIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Signed Up</p>
                  <p className="text-sm font-medium text-gray-900">
                    {user.signupDate || user.createdAt
                      ? (() => { const d = new Date(user.signupDate || user.createdAt); return `${d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago' })} ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago' })}`; })()
                      : '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <ClockIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Days Since Signup</p>
                  <p className="text-sm font-medium text-gray-900">
                    {daysSinceSignup !== null ? `${daysSinceSignup} days` : '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <ClockIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Days at Current Stage</p>
                  <p
                    className={`text-sm font-medium ${
                      daysAtCurrentStage > 7 ? 'text-red-600' : 'text-gray-900'
                    }`}
                  >
                    {daysAtCurrentStage !== null && daysAtCurrentStage !== undefined
                      ? `${daysAtCurrentStage} days`
                      : '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <ClockIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">State entered</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatTimestamp(stateEnteredAt)}
                  </p>
                </div>
              </div>
              {user.signupMethod && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <EnvelopeIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500">Signup Method</p>
                    <p className="text-sm font-medium text-gray-900 capitalize">
                      {user.signupMethod}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Section 2: Stage Progress Indicator */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Stage Progress</h3>

        {loading ? (
          <div className="animate-pulse">
            <div className="h-3 bg-gray-200 rounded-full w-full mb-6" />
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-8 bg-gray-100 rounded w-full" />
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Progress bar */}
            <div className="mb-6">
              {(() => {
                const isActivated = currentStage === 'activated';
                const pct = Math.round(((currentStageIdx + 1) / STAGE_ORDER.length) * 100);

                return (
                  <>
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                      <span>Progress</span>
                      <span>{isActivated ? 'Complete' : `${pct}%`}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full transition-all duration-500 ${
                          isActivated ? 'bg-green-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${isActivated ? 100 : pct}%` }}
                      />
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Stages list */}
            <div className="space-y-1">
              {STAGE_ORDER.map((stage) => {
                const status = getStageStatus(stage);
                return (
                  <div
                    key={stage}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg ${
                      status === 'current'
                        ? 'bg-blue-50 border border-blue-200'
                        : status === 'completed'
                        ? 'bg-green-50'
                        : 'bg-gray-50'
                    }`}
                  >
                    {/* Status icon */}
                    {status === 'completed' ? (
                      <CheckCircleSolidIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                    ) : status === 'current' ? (
                      <div className="w-5 h-5 rounded-full border-2 border-blue-500 bg-blue-500 flex items-center justify-center flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-white" />
                      </div>
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                    )}

                    {/* Label + timestamp */}
                    <div className="flex-1 min-w-0">
                      <span
                        className={`text-sm ${
                          status === 'current'
                            ? 'font-semibold text-blue-800'
                            : status === 'completed'
                            ? 'text-green-800'
                            : 'text-gray-400'
                        }`}
                      >
                        {STAGE_LABELS[stage]}
                      </span>
                      {(status === 'completed' || status === 'current') && evidencedStages.get(stage) && (
                        <span className="block text-xs text-gray-400 mt-0.5">
                          {formatStagetime(evidencedStages.get(stage))}
                        </span>
                      )}
                    </div>

                    {status === 'current' && (
                      <span className="ml-auto text-xs font-medium text-blue-600 flex-shrink-0">
                        Current
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Section 3: Journey Timeline */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Journey Timeline</h3>

        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-4">
                <div className="w-8 h-8 bg-gray-200 rounded-full flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                  <div className="h-3 bg-gray-100 rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : timeline.length === 0 ? (
          <div className="text-center py-8">
            <ClockIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No journey events recorded yet.</p>
            <p className="text-gray-400 text-sm mt-1">
              Events will appear here as this person progresses.
            </p>
          </div>
        ) : (
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-gray-200" />

            <div className="space-y-4">
              {timeline.map((event, idx) => {
                const eventType = event.eventType || event.action || event.type || '';
                const EventIcon = EVENT_ICONS[eventType] || ArrowRightIcon;
                const label =
                  EVENT_LABELS[eventType] || eventType.replace(/_/g, ' ');
                const metaDisplay = getMetadataDisplay(event);

                return (
                  <div key={idx} className="relative flex gap-4 pl-1">
                    {/* Timeline dot */}
                    <div className="relative z-10 w-8 h-8 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center flex-shrink-0">
                      <EventIcon className="w-4 h-4 text-gray-500" />
                    </div>

                    {/* Event content */}
                    <div className="flex-1 pb-4 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{label}</p>
                      {metaDisplay && (
                        <p className="text-xs text-gray-600 mt-0.5">{metaDisplay}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {formatTimestamp(event.timestamp || event.createdAt)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
